import React, { FC, useLayoutEffect, useMemo, useState } from 'react';
import { ChakraProvider } from '@chakra-ui/provider';
import { extendTheme } from '@chakra-ui/react';
import { EVENT_EVENTS } from 'Sp/Angular/Events/Client/Gallery';
import getTheme from 'ts/common/components/gallery/getTheme';
import {
    IMobileBlurSettings,
    MobileBlurContext,
    NO_MOBILE_BLUR
} from 'ts/common/components/gallery/screenshot-deterrence/MobileBlurContext';
import { isMobileDevice } from 'ts/common/components/gallery/screenshot-deterrence/detector';

let theme: ReturnType<typeof extendTheme> | undefined;

const useTheme = (
    spEventBus: SpAngularJs.ISpEventBus,
    providedEventTheme?: SpApi.Client.IEventTheme
) => {
    const [eventTheme, setEventTheme] = useState<SpApi.Client.IEventTheme | undefined>(
        providedEventTheme
    );
    const [mobileBlur, setMobileBlur] = useState(NO_MOBILE_BLUR);

    // A layout effect so that, when the event data is already loaded and GOT answers GET
    // synchronously, a newly mounted root's photos are blurred before its first paint
    useLayoutEffect(() => {
        const off = spEventBus.on<SpApi.Client.IEvent>(EVENT_EVENTS.GOT, (eventData) => {
            setEventTheme(eventData.theme);
            setMobileBlur((current) => {
                const next: IMobileBlurSettings = {
                    blurType: eventData.screenshotDeterrenceMobileType,
                    scope: eventData.screenshotDeterrenceMobileScope ?? 'all',
                    isMobile: isMobileDevice()
                };

                // GOT re-fires whenever any themed root mounts; keep the same object so blurred
                // photos don't all re-render
                return next.blurType === current.blurType &&
                    next.scope === current.scope &&
                    next.isMobile === current.isMobile
                    ? current
                    : next;
            });
        });

        if (!providedEventTheme) {
            spEventBus.emit(EVENT_EVENTS.GET);
        }

        return off;
    }, [providedEventTheme, spEventBus]);

    theme = useMemo(() => {
        return theme ?? (eventTheme && extendTheme(getTheme(eventTheme)));
    }, [eventTheme]);

    return { eventTheme, mobileBlur, theme };
};

/**
 * The gallery theme built for the current event, once a themed component has rendered. Lets
 * non-React callers - the Angular spToast service - theme what they hand to the toast host.
 */
export const getGalleryTheme = () => theme;

export const withTheme = <P extends object>(
    Component: FC<P>,
    providedGalleryTheme?: SpApi.Client.IEventTheme
): React.FC<P & { spEventBus: SpAngularJs.ISpEventBus }> =>
    function WithTheme({ spEventBus, ...props }) {
        const { mobileBlur, theme } = useTheme(spEventBus, providedGalleryTheme);

        // Use @chakra-ui/provider (not @chakra-ui/react) so we do not mount Chakra’s
        // ToastProvider per react2angular root. React’s ChakraProvider wraps ToastProvider;
        // Chakra’s toast store is a singleton, so N providers rendered the same toast N times.
        // `spEventBus` is forwarded (in addition to being used above) so wrapped components
        // that also declare it in their own props — e.g. to mute/unmute other audio sources —
        // can access it too; components that don't declare/destructure it are unaffected.
        return theme ? (
            <ChakraProvider cssVarsRoot="body" resetCSS={false} theme={theme}>
                <MobileBlurContext.Provider value={mobileBlur}>
                    <Component {...({ ...props, spEventBus } as P)} />
                </MobileBlurContext.Provider>
            </ChakraProvider>
        ) : null;
    };

export default useTheme;
